import type { GetVideoAssetsReframeMetadataQuery } from './generated/GetVideoAssetsReframeMetadata';

/**
 * Eligibility rules for Firefly video reframe, over the result of the
 * `GetVideoAssetsReframeMetadata` query.
 */

export type ReframeAsset = NonNullable<
  NonNullable<GetVideoAssetsReframeMetadataQuery['account']>['assets']
>['nodes'][number];

const VIDEO_CODEC_FIELD_NAME = 'Video Codec';

/** Max source assets `createReframeVideoOperation` accepts in one call. */
export const MAX_ASSETS_LIMIT_REFRAME_VIDEO = 100;

/** Reframe rejects clips longer than 30 minutes. */
export const MAX_DURATION_SECONDS = 1800;

const SUPPORTED_REFRAME_CODECS = [
  'h.264',
  'h264',
  'avc',
  'avc1',
  'h.265',
  'h265',
  'hevc',
  'hvc1',
] as const;

/**
 * Extracts the Video Codec value from asset field-value nodes.
 * @returns the codec string, or null if missing, empty, or not a text field.
 */
export function getVideoCodecFromNodes(
  nodes: Array<{
    fieldDefinition: { id: string; name: string };
    value?: { __typename?: string; text?: string | null } | null;
  }>
): string | null {
  const node = nodes.find(
    (n) => n.fieldDefinition?.name === VIDEO_CODEC_FIELD_NAME
  );
  const value = node?.value;
  if (
    value?.__typename === 'TextFieldValue' &&
    value.text != null &&
    value.text !== ''
  ) {
    return value.text;
  }
  return null;
}

/** Resolves the "Video Codec" system field's definition id, or undefined when absent. */
export function findVideoCodecFieldId(
  nodes: Array<{ id: string; name: string; isSystemField: boolean }>
): string | undefined {
  return nodes.find(
    (field) => field.name === VIDEO_CODEC_FIELD_NAME && field.isSystemField
  )?.id;
}

/** Whether the codec is supported for reframe (H.264/AVC or H.265/HEVC). */
export function isSupportedReframeCodec(codec: string | null): boolean {
  if (codec == null || codec === '') return false;
  const normalized = codec.trim().toLowerCase();
  return SUPPORTED_REFRAME_CODECS.some((s) => normalized === s);
}

export type ValidationErrorKind = 'over_duration' | 'invalid_codec';

export type FailedAsset = {
  id: string;
  name: string | null;
  errors: ValidationErrorKind[];
  codec?: string | null;
};

export type ProcessingAsset = {
  id: string;
  name: string | null;
};

export type ValidationResult = {
  failedAssets: FailedAsset[];
  processingAssets: ProcessingAsset[];
  validAssetIds: string[];
};

type ValidateReframeAssetsOptions = {
  /** Called with the normalized codec for each transcoded video whose codec is
   * present but unsupported. */
  onUnsupportedCodec?: (codec: string) => void;
};

/**
 * Splits reframe candidates into valid, processing, and failed.
 *
 * Assets whose transcode pipeline has not finished (`transcodedAt` is null) are
 * reported as processing rather than failed, because duration and codec
 * metadata are populated by the transcode pipeline.
 */
export function validateReframeAssets(
  assets: ReframeAsset[],
  options?: ValidateReframeAssetsOptions
): ValidationResult {
  const failedAssets: FailedAsset[] = [];
  const processingAssets: ProcessingAsset[] = [];
  const validAssetIds: string[] = [];

  for (const asset of assets) {
    if (asset.__typename !== 'VideoAsset') {
      failedAssets.push({
        id: asset.id,
        name: asset.name ?? null,
        errors: ['invalid_codec'],
        codec: null,
      });
    } else if (asset.media?.transcodedAt == null) {
      processingAssets.push({ id: asset.id, name: asset.name ?? null });
    } else {
      const errors: ValidationErrorKind[] = [];

      if (
        asset.media.duration == null ||
        asset.media.duration > MAX_DURATION_SECONDS
      ) {
        errors.push('over_duration');
      }

      const codec = getVideoCodecFromNodes(asset.fieldValuesNew?.nodes ?? []);
      if (!isSupportedReframeCodec(codec)) {
        errors.push('invalid_codec');
        if (codec != null && codec !== '') {
          options?.onUnsupportedCodec?.(codec.trim().toLowerCase());
        }
      }

      if (errors.length > 0) {
        failedAssets.push({
          id: asset.id,
          name: asset.name ?? null,
          errors,
          codec: errors.includes('invalid_codec') ? codec : undefined,
        });
      } else {
        validAssetIds.push(asset.id);
      }
    }
  }

  return { failedAssets, processingAssets, validAssetIds };
}
